import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, Heart, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatronsManagementPanel = () => {
  const [patrons, setPatrons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExamples, setShowExamples] = useState(true);
  const [editingPatron, setEditingPatron] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPatron, setNewPatron] = useState({
    name: '',
    type: 'business',
    tier: 'supporter',
    contribution_type: 'financial',
    visibility: 'public',
    status: 'active'
  });

  const fetchPatrons = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/patrons?include_examples=${showExamples}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPatrons(data);
      }
    } catch (error) {
      console.error('Error fetching patrons:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatrons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExamples]);

  const markAllExamples = async () => {
    try {
      const response = await fetch(`${API_URL}/api/patrons/mark-examples`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchPatrons();
      } else {
        toast.error('Failed to mark examples');
      }
    } catch (error) {
      toast.error('Error marking examples');
    }
  };

  const toggleExampleFlag = async (patronId, isExample) => {
    try {
      const response = await fetch(`${API_URL}/api/patrons/${patronId}/example?is_example=${isExample}`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success(isExample ? 'Marked as example' : 'Unmarked as example');
        fetchPatrons();
      } else {
        toast.error('Failed to update');
      }
    } catch (error) {
      toast.error('Error updating patron');
    }
  };

  const deletePatron = async (patronId) => {
    if (!window.confirm('Are you sure you want to delete this patron?')) return;
    try {
      const response = await fetch(`${API_URL}/api/patrons/${patronId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Patron deleted');
        fetchPatrons();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Error deleting patron');
    }
  };

  const createPatron = async () => {
    try {
      const response = await fetch(`${API_URL}/api/patrons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newPatron)
      });
      if (response.ok) {
        toast.success('Patron created');
        setShowCreateDialog(false);
        setNewPatron({
          name: '',
          type: 'business',
          tier: 'supporter',
          contribution_type: 'financial',
          visibility: 'public',
          status: 'active'
        });
        fetchPatrons();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create patron');
      }
    } catch (error) {
      toast.error('Error creating patron');
    }
  };

  const tierColors = {
    'champion': 'bg-yellow-600',
    'partner': 'bg-purple-600',
    'supporter': 'bg-blue-600',
    'friend': 'bg-green-600'
  };

  return (
    <div className="space-y-6">
      {/* Actions Bar */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Label className="text-slate-300 flex items-center gap-2">
                <Switch
                  checked={showExamples}
                  onCheckedChange={setShowExamples}
                />
                Show examples
              </Label>
              <Badge variant="outline" className="text-slate-400">
                {patrons.length} patrons
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={markAllExamples}
                className="border-amber-600 text-amber-400 hover:bg-amber-600/20"
              >
                <EyeOff className="w-4 h-4 mr-1" />
                Mark Seed Data
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCreateDialog(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Patron
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patrons Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-500" />
            Patrons & Partners
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Type</TableHead>
                  <TableHead className="text-slate-400">Tier</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Example?</TableHead>
                  <TableHead className="text-slate-400">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {patrons.map((patron) => (
                  <TableRow key={patron.id} className={`border-slate-700 ${patron.is_example ? 'opacity-60' : ''}`}>
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{patron.name}</p>
                        {patron.tagline && <p className="text-xs text-slate-400">{patron.tagline}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300 capitalize">{patron.type}</TableCell>
                    <TableCell>
                      <Badge className={`${tierColors[patron.tier] || 'bg-slate-600'} text-white text-xs`}>
                        {patron.tier}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={patron.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                        {patron.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {patron.is_example ? (
                        <Badge variant="outline" className="border-amber-500 text-amber-400 text-xs">Example</Badge>
                      ) : (
                        <Badge className="bg-green-600 text-xs">Real</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleExampleFlag(patron.id, !patron.is_example)}
                          className="text-slate-300 hover:text-white"
                          title={patron.is_example ? 'Mark as real' : 'Mark as example'}
                        >
                          {patron.is_example ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deletePatron(patron.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Patron Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add New Patron</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a real patron or partner to display on the site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={newPatron.name}
                onChange={(e) => setNewPatron({...newPatron, name: e.target.value})}
                placeholder="Organization or person name"
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Type</Label>
                <select
                  value={newPatron.type}
                  onChange={(e) => setNewPatron({...newPatron, type: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                >
                  <option value="business">Business</option>
                  <option value="individual">Individual</option>
                  <option value="ngo">NGO</option>
                  <option value="foundation">Foundation</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300">Tier</Label>
                <select
                  value={newPatron.tier}
                  onChange={(e) => setNewPatron({...newPatron, tier: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                >
                  <option value="champion">Champion</option>
                  <option value="partner">Partner</option>
                  <option value="supporter">Supporter</option>
                  <option value="friend">Friend</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Tagline</Label>
              <Input
                value={newPatron.tagline || ''}
                onChange={(e) => setNewPatron({...newPatron, tagline: e.target.value})}
                placeholder="Short description"
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Website URL</Label>
              <Input
                value={newPatron.website_url || ''}
                onChange={(e) => setNewPatron({...newPatron, website_url: e.target.value})}
                placeholder="https://..."
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={createPatron}
              disabled={!newPatron.name}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Create Patron
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Tickets Panel Component

export default PatronsManagementPanel;
